import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { useAuth } from './AuthContext';

// Roles come from server session, no direct client-side queries
import { UserRole } from '@/components/authnz/types/rbac';
import { useSettings } from '@/components/settings/lib/settings-context';

interface AdminContextType {
  isAdminMode: boolean;
  isAuthorizedAdmin: boolean;
  isAdminReady: boolean;
  userEmail: string | null;
  toggleAdminMode: () => void;
  setAdminMode: (mode: boolean) => void;
}
import logger from '@/lib/logging';

/** Exported for optional-hooks.ts — prefer useAdmin() in normal usage. */
export const AdminContext = createContext<AdminContextType | undefined>(undefined);

export function AdminProvider({ children }: { children: ReactNode }) {
  const { user: currentUser, serverUser, userContext } = useAuth();
  const { settings, setSettings, isLoading: settingsLoading } = useSettings();
  const [adminModeOverride, setAdminModeOverride] = useState<boolean | null>(null);

  const userEmail = currentUser?.email || userContext?.user?.email || null;
  const userRoles = (serverUser?.roles || []) as UserRole[];

  const isAuthorizedAdminUser = useMemo(
    () =>
      Boolean(
        userEmail &&
          currentUser?.uid &&
          userRoles.includes(UserRole.ADMINISTRATOR),
      ),
    [userEmail, currentUser?.uid, userRoles.join(',')],
  );

  const isAdminReady = !settingsLoading;

  const isAdminMode = useMemo(() => {
    if (!isAuthorizedAdminUser || settingsLoading) {
      return false;
    }

    return adminModeOverride ?? Boolean(settings.isAdminMode);
  }, [
    adminModeOverride,
    isAuthorizedAdminUser,
    settings.isAdminMode,
    settingsLoading,
  ]);

  useEffect(() => {
    setAdminModeOverride(null);
  }, [currentUser?.uid, settings.isAdminMode, settingsLoading]);

  useEffect(() => {
    logger.debug('[AdminContext] Admin status:', {
      email: userEmail,
      uid: currentUser?.uid,
      userRoles,
      isAuthorizedAdminUser,
      settingsIsAdminMode: settings.isAdminMode,
      settingsLoading,
      isAdminMode,
      isAdminReady,
    });
  }, [
    userEmail,
    currentUser?.uid,
    userRoles.join(','),
    isAuthorizedAdminUser,
    settings.isAdminMode,
    settingsLoading,
    isAdminMode,
    isAdminReady,
  ]);

  const toggleAdminMode = () => {
    if (isAuthorizedAdminUser) {
      const newMode = !isAdminMode;
      setAdminModeOverride(newMode);
      setSettings(prev => ({ ...prev, isAdminMode: newMode }));
    } else {
      logger.warn('User does not have Administrator role - cannot toggle admin mode');
    }
  };

  const setAdminMode = (mode: boolean) => {
    if (isAuthorizedAdminUser) {
      setAdminModeOverride(mode);
      setSettings(prev => ({ ...prev, isAdminMode: mode }));
    } else {
      logger.warn('User does not have Administrator role - cannot set admin mode');
    }
  };

  return (
    <AdminContext.Provider
      value={{
        isAdminMode,
        isAuthorizedAdmin: isAuthorizedAdminUser,
        isAdminReady,
        userEmail,
        toggleAdminMode,
        setAdminMode
      }}
    >
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
}
